import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
  StyledSubTableContainer,
} from "@/common/styles";
import {
  getOptionsFromRefList,
  hasCheckSecurity,
} from "@/util/common_function";
import Modal from "@/common/elements/Modal";
import Button from "@/common/elements/Button";
import JailIncidentsInvolvedDetail from "./JailIncidentsInvolvedDetail";
import DataTable from "@/common/elements/DataTable";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import { MODULES_LIST_WITH_ID } from "@/util/common_variable";
import { v4 as uuidV4 } from "uuid";
import { Popconfirm } from "antd";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const JAIL_INCIDENTS_HEADERS = [
  { key: "jailIncidentID", text: "#" },
  { key: "dateAdded", text: "Date" },
  { key: "inmateName", text: "Name" },
  { key: "involvement", text: "Involvement" },
  { key: "disciplinary", text: "Disciplinary" },
  { key: "synopsis", text: "Synopsis" },
];

const JailIncidentsDetails = ({ startingPoint, jailIncidentsDetailsRef }) => {
  const bookings = useSelector((state) => state.bookings?.bookings);
  // Drives isFieldEnable in JailIncidentInvolvedInmateInformation (Different
  // Booking button etc.) - previously never passed through, so that button
  // was always disabled regardless of whether this Booking is in Modify mode.
  const { bookingsFieldsEnabled } = useSelector((state) => state.bookings);
  const [selectedInvolvedInmatesData, setSelectedInvolvedInmatesData] = useState(null);
  const [showInvolvedInmate, setShowInvolvedInmate] = useState(false);

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md). Also replaces this
  // component's own dataTableRef — the hook's ref serves the same purpose
  // (passed straight to <DataTable ref={...} .../>), so `.resetAll()`
  // below still works unchanged.
  const {
    headers: jailIncidentsHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig(
    "Bookings:JailIncidentsInvolvedInmate",
    JAIL_INCIDENTS_HEADERS
  );

  const [tableData, setTableData] = useState({
    rows: [],
  });

  useEffect(() => {
    if (bookings?.jailIncidentsInvolvedInmate?.length > 0) {
      const rows = bookings?.jailIncidentsInvolvedInmate?.map((incident) => ({
        ...incident,
        uuid: uuidV4(),
        id: incident?.jailIncidentID || "-",
        incidentNumber: incident?.incidentNumber || "-",
        date: incident?.reportDate || "-",
        involvement: incident?.jailIncidentsInvolvementType || "-",
        disciplinary: incident?.disciplineRequired || "-",
        synopsis: incident?.synopsis || "-",
      }));
      setTableData((prev) => ({ ...prev, rows }));
    } else {
      setTableData((prev) => ({ ...prev, rows: [] }));
      setSelectedInvolvedInmatesData(null);
    }
  }, [bookings]);

  const onHandleIncidentView = () => {
    if (selectedInvolvedInmatesData) {
      setShowInvolvedInmate(true);
    }
  };

  const onCancel = () => {
    setSelectedInvolvedInmatesData(null);
    dataTableRef.current.resetAll();
    setShowInvolvedInmate(false);
  };

  const onRowClick = (row, index) => {
    setSelectedInvolvedInmatesData(row);
    console.log("index : ", index);
    if(index !== undefined && index !== null) {
      onHandleIncidentView();
    }
  };

  return (
    <>
      <StyledSectionHeading marginTop="0px">
        <span>Jail Incidents</span>
      </StyledSectionHeading>

      <StyledSubTableContainer>
        <StyledFlex align="center" justify="space-between">
          <h2>Jail Incidents Involved Inmate</h2>

          <StyledFlex marginBottom="5px">
            <Button
              onClick={onHandleIncidentView}
              style={{ marginRight: "5px" }}
              disabled={
                selectedInvolvedInmatesData === null ||
                isConfiguring ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.module,
                  LABELS_CAPITAL.ADD,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                )
              }
            >
              Details
            </Button>
            <Button
              onClick={onCancel}
              disabled={selectedInvolvedInmatesData === null || isConfiguring}
            >
              Cancel
            </Button>

            {/* Config — drag-reorder/resize columns; only while Modify is active */}
            {!isConfiguring ? (
              <Button
                style={{ marginLeft: "5px" }}
                htmlType="button"
                onClick={startConfiguring}
              >
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Do you want to save the current list view configuration?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button
                  style={{ marginLeft: "5px" }}
                  htmlType="button"
                >
                  {configButtonLabel}
                </Button>
              </Popconfirm>
            )}
          </StyledFlex>
        </StyledFlex>

        <DataTable
          ref={dataTableRef}
          headers={jailIncidentsHeaders}
          draggable={isConfiguring}
          data={tableData.rows}
          rowUniqueId="uuid"
          onRowClick={(row, index) => onRowClick(row, index)}
          // onRowDoubleClick={(data) => {
          //   setSelectedInvolvedInmatesData(data);
          //   onHandleIncidentView();
          // }}
          selected={selectedInvolvedInmatesData === null}
        />
      </StyledSubTableContainer>

      {showInvolvedInmate && (
        <Modal
          designPattern="ant"
          header="Jail Incidents Involved Inmate Details"
          show={showInvolvedInmate}
          onHide={onCancel}
          handleCancel={onCancel}
          size="xl"
          width="75%"
          footerActions={[
            <Button
              type="secondary"
              color="secondary"
              onClick={onCancel}
              key="close"
            >
              Close
            </Button>,
          ]}
        >
          {/* Involved Detail */}
          <JailIncidentsInvolvedDetail
            data={selectedInvolvedInmatesData}
            startingPoint={startingPoint}
            isModify={bookingsFieldsEnabled}
          />
        </Modal>
      )}
    </>
  );
};

export default JailIncidentsDetails;
